import axios from 'axios';
import { API_CONFIG, buildApiUrl, getTimeout } from '../config/apiConfig';
import { COMPANY_ID_MAPPINGS } from '../constants/companyIds';

// Serviço de usuários (cadastro)
class UserService {
  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
    this.timeout = getTimeout('default');
  }

  async register(userData) {
    // Adicionar permissão padrão se não informada
    const dataToRegister = {
      ...userData,
      allowedOsTypes: userData.allowedOsTypes || 'Todas'
    };

    // Salvar localmente também para persistência nesta demo
    this.saveUserLocal(dataToRegister);

    // Tentar endpoints conhecidos e variações comuns
    const baseCandidates = [
      API_CONFIG.ENDPOINTS.USERS || '/users',
      '/api/users',
      API_CONFIG.ENDPOINTS?.REGISTER ?? '/auth/register',
      '/api/auth/register',
      '/auth/signup',
      '/register'
    ];

    // ... (rest of endpointsToTry logic)
    const endpointsToTry = Array.from(new Set([
      ...baseCandidates,
      ...baseCandidates.map(p => (p.endsWith('/') ? p : `${p}/`))
    ]));

    for (const ep of endpointsToTry) {
      const url = buildApiUrl(ep);
      try {
        // Montar headers, anexando Authorization se houver token
        const token = localStorage.getItem('auth_token');
        const headers = { ...API_CONFIG.DEFAULT_HEADERS };
        if (token && token !== 'null' && token !== 'undefined') {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await axios.post(
          url,
          dataToRegister,
          {
            headers,
            timeout: this.timeout
          }
        );

        return {
          success: true,
          user: response.data,
          message: 'Usuário cadastrado com sucesso'
        };
      } catch (error) {
        // Se for 404, tentar próximo endpoint
        if (error?.response?.status === 404) {
          console.warn(`Endpoint não encontrado: ${url}. Tentando próximo...`);
          continue;
        }

        let errorMessage = 'Erro ao cadastrar usuário';
        if (error.response) {
          const status = error.response.status;
          const data = error.response.data;
          switch (status) {
            case 400:
              errorMessage = data?.message || 'Dados inválidos';
              break;
            case 401:
              errorMessage = data?.message || 'Não autorizado';
              if ((data?.message || '').toLowerCase().includes('token')) {
                errorMessage = 'Cadastro requer autenticação. Faça login com conta autorizada ou peça ao administrador.';
              }
              break;
            case 403:
              errorMessage = data?.message || 'Acesso negado';
              if ((data?.message || '').toLowerCase().includes('token')) {
                errorMessage = 'Cadastro requer autenticação. Faça login com conta autorizada ou peça ao administrador.';
              }
              break;
            case 409:
              errorMessage = data?.message || 'Usuário já existe';
              break;
            case 500:
              errorMessage = 'Erro interno do servidor';
              break;
            default:
              errorMessage = data?.message || `Erro ${status}`;
          }
        } else if (error.code === 'ECONNABORTED') {
          errorMessage = 'Timeout: Servidor demorou para responder';
        } else if (error.request) {
          errorMessage = 'Erro de conexão: Não foi possível conectar ao servidor';
        }

        return { success: false, error: errorMessage, message: errorMessage };
      }
    }

    // Se nenhum endpoint funcionou, retornamos sucesso se conseguimos salvar localmente
    return { 
      success: true, 
      user: dataToRegister, 
      message: 'Usuário cadastrado localmente (Endpoint de API não disponível)' 
    };
  }

  // Métodos para gerenciamento local (fallback/mock)
  getUsers() {
    const usersJson = localStorage.getItem('managed_users');
    return usersJson ? JSON.parse(usersJson) : [];
  }

  saveUserLocal(user) {
    const users = this.getUsers();
    const existingIndex = users.findIndex(u => u.email === user.email);
    
    if (existingIndex >= 0) {
      users[existingIndex] = { ...users[existingIndex], ...user };
    } else {
      users.push({ ...user, id: Date.now().toString() });
    }
    
    localStorage.setItem('managed_users', JSON.stringify(users));
  }

  deleteUser(id) {
    const users = this.getUsers();
    const filtered = users.filter(u => u.id !== id);
    localStorage.setItem('managed_users', JSON.stringify(filtered));
    return { success: true, message: 'Usuário removido com sucesso' };
  }

  updateUserPermissions(id, allowedOsTypes) {
    const users = this.getUsers();
    const index = users.findIndex(u => u.id === id);
    if (index >= 0) {
      users[index].allowedOsTypes = allowedOsTypes;
      
      // Também atualizar allowedCompanies baseado no mapeamento
      let allowedCompanies = [];
      if (allowedOsTypes === 'Todas') {
        allowedCompanies = [
          ...COMPANY_ID_MAPPINGS['Cia. Zaffari'],
          ...COMPANY_ID_MAPPINGS['Comercial Zaffari'],
          ...COMPANY_ID_MAPPINGS['Stok Center']
        ];
      } else {
        allowedCompanies = COMPANY_ID_MAPPINGS[allowedOsTypes] || [];
      }
      
      users[index].allowedCompanies = allowedCompanies;
      localStorage.setItem('managed_users', JSON.stringify(users));
      return { success: true, message: 'Permissões atualizadas com sucesso', user: users[index] };
    }
    return { success: false, error: 'Usuário não encontrado' };
  }
}

const userService = new UserService();
export default userService;